import { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Page } from '@Components/Page';
import { BPMTable } from '@Components/StickyTable';
import { TableLoading } from '@Components/TableEmptyState';
import { TablePage } from '@Components/TablePage';
import { useSetError } from '@src/redux/modals';
import { download } from '@src/utils/download-utils';
import { HEADER_HEIGHT, PAGE_TITLE, ROW_HEIGHT } from './finance-constants';
import { financeHeaders } from './finance-headers';
import { useFinanceInvoices } from './use-finance-invoices';

const CSV_MENU_OPTIONS = ['CSV'];

export const Finance = (): JSX.Element => {
  const setError = useSetError();
  const { data, isLoading, error } = useFinanceInvoices();
  const [csvContent, setCsvContent] = useState('');

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load finance invoices",
        message: error.message,
        reportError: error
      });
    }
  }, [error, setError]);

  const onCsvChange = useCallback((csv: string) => setCsvContent(csv), []);
  const csvClickOptions = useMemo(
    () => [() => download(csvContent, 'Finance Signed Off Bills.csv', 'text/csv')],
    [csvContent]
  );

  const renderTableLoading = useCallback(
    () => (
      <TableLoading
        isLoading={isLoading}
        error={error}
        rowHeight={ROW_HEIGHT}
        headerHeight={HEADER_HEIGHT}
        emptyMessage='No invoices to show for the approved clients.'
      />
    ),
    [isLoading, error]
  );

  const tableControls = useMemo(
    () => (
      <DownloadDropdown
        disabled={isLoading || !data?.length}
        menuOptions={CSV_MENU_OPTIONS}
        onClickOptions={csvClickOptions}
      />
    ),
    [isLoading, data, csvClickOptions]
  );

  return (
    <Page title={PAGE_TITLE} pageType={PAGE_TITLE} isLoading={isLoading}>
      <TablePage dataTestId='finance-table'>
        <BPMTable
          alternateColors
          headers={financeHeaders}
          data={data ?? []}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          onCsvChange={onCsvChange}
          noRowsRenderer={renderTableLoading}
          additionalControls={tableControls}
        />
      </TablePage>
    </Page>
  );
};
